import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
    Container,
    Typography,
    Paper,
    Box,
    Button,
    CircularProgress,
    Alert,
    Divider,
    TextField,
    Breadcrumbs,
    Stack,
    Grid,
    FormControl,
    InputLabel,
    Select,
    SelectChangeEvent,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    List,
    ListItem,
    ListItemText,
    IconButton,
    Chip,
    Checkbox,
    FormControlLabel
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { membershipApplicationAPI } from '../../services/api';
import { MembershipApplication, ApplicationStatus, Discount, OneTimeFee, MemberGroup, MembershipTier } from '../../types';
import { oneTimeFeeAPI, memberGroupAPI, discountAPI, membershipTierAPI } from '../../services/api';

const CreateMemberAccountPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [application, setApplication] = useState<MembershipApplication | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [formData, setFormData] = useState<any>({});
    const [oneTimeFees, setOneTimeFees] = useState<OneTimeFee[]>([]);
    const [selectedFees, setSelectedFees] = useState<OneTimeFee[]>([]);
    const [discounts, setDiscounts] = useState<Discount[]>([]);
    const [selectedDiscounts, setSelectedDiscounts] = useState<Discount[]>([]);
    const [memberGroups, setMemberGroups] = useState<MemberGroup[]>([]);
    const [selectedGroup, setSelectedGroup] = useState<string>('');
    const [membershipTiers, setMembershipTiers] = useState<MembershipTier[]>([]);
    const [selectedTierId, setSelectedTierId] = useState<string>('');
    const [membershipStartDate, setMembershipStartDate] = useState<string>('');
    const [membershipEndDate, setMembershipEndDate] = useState<string>('');
    const [showEndDate, setShowEndDate] = useState<boolean>(false);

    // Dialogs
    const [feeDialogOpen, setFeeDialogOpen] = useState(false);
    const [discountDialogOpen, setDiscountDialogOpen] = useState(false);

    // Fetch application details
    useEffect(() => {
        const fetchApplication = async () => {
            if (!id) return;

            setLoading(true);
            try {
                const response = await membershipApplicationAPI.getApplicationById(id);
                setApplication(response.data);

                // Initialize form data from application
                setFormData({
                    firstName: response.data.firstName,
                    lastName: response.data.lastName,
                    email: response.data.email,
                    phone: response.data.phone || '',
                    mobile: response.data.mobile || '',
                    addressLine1: response.data.addressLine1 || '',
                    addressLine2: response.data.addressLine2 || '',
                    city: response.data.city || '',
                    province: response.data.province || '',
                    postalCode: response.data.postalCode || '',
                    country: response.data.country || '',
                    membershipTierId: response.data.membershipTierId || '',
                    membershipTierName: response.data.membershipTierName || '',
                    monthlyPrice: response.data.monthlyPrice || 0,
                });

                // Set the selected tier ID with empty string fallback
                setSelectedTierId(response.data.membershipTierId || '');

                setLoading(false);
            } catch (err) {
                console.error('Error fetching application details:', err);
                setError('Failed to load application details');
                setLoading(false);
            }
        };

        fetchApplication();

        // Fetch one-time fees, discounts, and member groups
        const fetchAdditionalData = async () => {
            try {
                const [feesResponse, discountsResponse, groupsResponse, tiersResponse] = await Promise.all([
                    oneTimeFeeAPI.getAll().catch(() => ({ data: [] })),
                    discountAPI.getAll().catch(() => ({ data: [] })),
                    memberGroupAPI.getAll().catch(() => ({ data: [] })),
                    membershipTierAPI.getMembershipTiers().catch(() => ({ data: [] }))
                ]);

                setOneTimeFees(feesResponse.data || []);
                setDiscounts(discountsResponse.data || []);
                setMemberGroups(groupsResponse.data || []);
                setMembershipTiers(tiersResponse.data || []);

                // Validate selectedTierId against available membership tiers
                if (application?.membershipTierId) {
                    const tierExists = tiersResponse.data?.some(
                        (tier: MembershipTier) => tier.id === application.membershipTierId
                    );

                    setSelectedTierId(tierExists ? application.membershipTierId : '');
                }
            } catch (err) {
                console.error('Error fetching additional data:', err);
                // Ensure arrays are initialized even on error
                setOneTimeFees([]);
                setDiscounts([]);
                setMemberGroups([]);
                setMembershipTiers([]);
            }
        };

        fetchAdditionalData();
    }, [id, application?.membershipTierId]);

    // Handle form field changes
    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value,
        });
    };    // Handle select changes
    const handleSelectChange = (event: SelectChangeEvent) => {
        const name = event.target.name as string;
        const value = event.target.value;

        if (name === 'memberGroup') {
            setSelectedGroup(value);
        } else if (name === 'membershipTierId') {
            setSelectedTierId(value);
        } else {
            setFormData({
                ...formData,
                [name]: value,
            });
        }
    };

    // Handle membership tier change
    const handleTierChange = (event: SelectChangeEvent) => {
        const tierId = event.target.value;
        setSelectedTierId(tierId);

        // Find the selected tier to update price
        const selectedTier = membershipTiers.find(tier => tier.id === tierId);
        if (selectedTier) {
            setFormData({
                ...formData,
                membershipTierId: selectedTier.id,
                membershipTierName: selectedTier.name,
                monthlyPrice: selectedTier.monthlyPrice
            });
        }
    };

    // Handle membership start date change
    const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setMembershipStartDate(e.target.value);
        if (error) setError(null); // Clear error when user makes changes
    };

    // Handle membership end date change
    const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setMembershipEndDate(e.target.value);
        if (error) setError(null); // Clear error when user makes changes
    };

    // Handle end date checkbox change
    const handleEndDateCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setShowEndDate(e.target.checked);
        if (!e.target.checked) {
            setMembershipEndDate('');
        }
    };

    // Get minimum date (today)
    const getMinDate = () => {
        const today = new Date();
        return today.toISOString().split('T')[0];
    };

    // Toggle fee dialog
    const handleFeeDialogToggle = () => {
        setFeeDialogOpen(!feeDialogOpen);
    };

    // Toggle discount dialog
    const handleDiscountDialogToggle = () => {
        setDiscountDialogOpen(!discountDialogOpen);
    };

    // Add one-time fee
    const handleAddFee = (fee: OneTimeFee) => {
        setSelectedFees([...selectedFees, fee]);
        setFeeDialogOpen(false);
    };

    // Add discount
    const handleAddDiscount = (discount: Discount) => {
        setSelectedDiscounts([...selectedDiscounts, discount]);
        setDiscountDialogOpen(false);
    };

    // Remove one-time fee
    const handleRemoveFee = (feeId: number) => {
        setSelectedFees(selectedFees.filter(fee => fee.id !== feeId));
    };

    // Remove discount
    const handleRemoveDiscount = (discountId: string) => {
        setSelectedDiscounts(selectedDiscounts.filter(discount => discount.id !== discountId));
    };

    // Calculate total with fees and discounts
    const calculateTotal = () => {
        const basePrice = parseFloat(formData.monthlyPrice?.toString() || '0');

        // Add fees
        const feesTotal = selectedFees.reduce((sum, fee) => sum + parseFloat(fee.amount.toString()), 0);

        // Subtract discounts
        const discountTotal = selectedDiscounts.reduce((sum, discount) => {
            if (discount.category === 'percentage') {
                return sum + (basePrice * parseFloat(discount.value.toString()) / 100);
            } else {
                return sum + parseFloat(discount.value.toString());
            }
        }, 0);

        return (basePrice + feesTotal - discountTotal).toFixed(2);
    };

    // Handle form submission
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!id) return;

        // Validate required fields
        if (!membershipStartDate) {
            setError('Membership Start Date is required');
            return;
        }

        // Validate that end date is after start date if both are provided
        if (showEndDate && membershipEndDate && membershipEndDate <= membershipStartDate) {
            setError('Membership End Date must be after Start Date');
            return;
        }

        try {
            // Construct the payload for creating a user account from the application
            const payload = {
                ...formData,
                membershipApplication: id,
                selectedFees: selectedFees.map(fee => fee.id),
                selectedDiscounts: selectedDiscounts.map(discount => discount.id),
                memberGroupId: selectedGroup || null,
                membershipTierId: selectedTierId || null,
                membershipStartDate: membershipStartDate || null,
                membershipEndDate: showEndDate ? membershipEndDate || null : null,
                role: 'member', // Default role for new members
            };

            // Call API to create user account
            await membershipApplicationAPI.createUserFromApplication(payload);

            // Navigate back to applications list after successful creation
            navigate('/membership-applications');
        } catch (err: any) {
            console.error('Error creating user account:', err);
            setError(err?.response?.data?.message || 'Failed to create user account');
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    // Format currency
    const formatCurrency = (amount: string | number | undefined) => {
        if (amount === undefined) return '$0.00';
        const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(numericAmount);
    };

    return (
        <Container>
            <Box sx={{ mt: 4, mb: 4 }}>
                <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
                    <RouterLink
                        className="user-link"
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            color: 'text.primary',
                            textDecoration: 'none',
                        }}
                        to="/dashboard"
                    >
                        <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                        Dashboard
                    </RouterLink>
                    <RouterLink
                        className="user-link"
                        to="/membership-applications"
                        style={{
                            color: 'text.primary',
                            textDecoration: 'none',
                        }}
                    >
                        Applications
                    </RouterLink>
                    <Typography color="text.primary">Create Member Account</Typography>
                </Breadcrumbs>

                <Typography variant="h4" gutterBottom sx={{ mt: 2 }}>
                    Create Member Account
                </Typography>

                {error && <Alert severity="error" sx={{ mt: 2, mb: 2 }}>{error}</Alert>}
            </Box>

            {loading ? (
                <Box display="flex" justifyContent="center" my={4}>
                    <CircularProgress />
                </Box>
            ) : application ? (
                <form onSubmit={handleSubmit}>
                    <Paper sx={{ p: 3, mb: 3 }}>
                        <Typography variant="h5" gutterBottom>
                            Personal Information
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        <Grid container spacing={3}>
                            <Grid size={{ xs: 12, md: 6 }} >
                                <TextField
                                    required
                                    fullWidth
                                    label="First Name"
                                    name="firstName"
                                    value={formData.firstName || ''}
                                    onChange={handleChange}
                                />
                            </Grid>

                            <Grid size={{ xs: 12, md: 6 }} >
                                <TextField
                                    required
                                    fullWidth
                                    label="Last Name"
                                    name="lastName"
                                    value={formData.lastName || ''}
                                    onChange={handleChange}
                                />
                            </Grid>

                            <Grid size={{ xs: 12, md: 6 }} >
                                <TextField
                                    required
                                    fullWidth
                                    label="Email"
                                    name="email"
                                    type="email"
                                    value={formData.email || ''}
                                    onChange={handleChange}
                                />
                            </Grid>

                            <Grid size={{ xs: 12, md: 6 }} >
                                <TextField
                                    fullWidth
                                    label="Phone"
                                    name="phone"
                                    value={formData.phone || ''}
                                    onChange={handleChange}
                                />
                            </Grid>

                            <Grid size={{ xs: 12, md: 6 }} >
                                <TextField
                                    fullWidth
                                    label="Mobile"
                                    name="mobile"
                                    value={formData.mobile || ''}
                                    onChange={handleChange}
                                />
                            </Grid>
                        </Grid>
                    </Paper>

                    <Paper sx={{ p: 3, mb: 3 }}>
                        <Typography variant="h5" gutterBottom>
                            Address
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        <Grid container spacing={3}>
                            <Grid size={{ xs: 12 }} >
                                <TextField
                                    fullWidth
                                    label="Address Line 1"
                                    name="addressLine1"
                                    value={formData.addressLine1 || ''}
                                    onChange={handleChange}
                                />
                            </Grid>
                            <Grid size={{ xs: 12 }} >
                                <TextField
                                    fullWidth
                                    label="Address Line 2"
                                    name="addressLine2"
                                    value={formData.addressLine2 || ''}
                                    onChange={handleChange}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }} >
                                <TextField
                                    fullWidth
                                    label="City"
                                    name="city"
                                    value={formData.city || ''}
                                    onChange={handleChange}
                                />
                            </Grid>

                            <Grid size={{ xs: 12, md: 6 }} >
                                <TextField
                                    fullWidth
                                    label="Province/State"
                                    name="province"
                                    value={formData.province || ''}
                                    onChange={handleChange}
                                />
                            </Grid>

                            <Grid size={{ xs: 12, md: 6 }} >
                                <TextField
                                    fullWidth
                                    label="Postal/Zip Code"
                                    name="postalCode"
                                    value={formData.postalCode || ''}
                                    onChange={handleChange}
                                />
                            </Grid>

                            <Grid size={{ xs: 12, md: 6 }} >
                                <TextField
                                    fullWidth
                                    label="Country"
                                    name="country"
                                    value={formData.country || ''}
                                    onChange={handleChange}
                                />
                            </Grid>
                        </Grid>
                    </Paper>

                    <Paper sx={{ p: 3, mb: 3 }}>
                        <Typography variant="h5" gutterBottom>
                            Membership Information
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        <Grid container spacing={3}>
                            <Grid size={{ xs: 12, md: 6 }} >
                                <FormControl fullWidth>
                                    <InputLabel id="membership-tier-label">Membership Tier</InputLabel>
                                    <Select
                                        labelId="membership-tier-label"
                                        id="membership-tier"
                                        name="membershipTierId"
                                        value={selectedTierId || ''}
                                        onChange={handleTierChange}
                                        label="Membership Tier"
                                    >
                                        <MenuItem value="">
                                            <em>None</em>
                                        </MenuItem>
                                        {membershipTiers.map((tier) => (
                                            <MenuItem key={tier.id} value={tier.id}>
                                                {tier.name} ({formatCurrency(tier.monthlyPrice)})
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }} >
                                <TextField
                                    fullWidth
                                    label="Monthly Price"
                                    name="monthlyPrice"
                                    value={formatCurrency(formData.monthlyPrice)}
                                    InputProps={{ readOnly: true }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }} >
                                <FormControl fullWidth>
                                    <InputLabel id="member-group-label">Add to Group Account</InputLabel>
                                    <Select
                                        labelId="member-group-label"
                                        id="member-group"
                                        name="memberGroup"
                                        value={selectedGroup}
                                        onChange={handleSelectChange}
                                        label="Add to Group Account"
                                    >
                                        <MenuItem value="">
                                            <em>None</em>
                                        </MenuItem>
                                        {memberGroups.map((group) => (
                                            <MenuItem key={group.id} value={group.id.toString()}>
                                                {group.groupName} ({group.groupType})
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }} >
                                <TextField
                                    required
                                    fullWidth
                                    label="Membership Start Date"
                                    name="membershipStartDate"
                                    type="date"
                                    value={membershipStartDate}
                                    onChange={handleStartDateChange}
                                    InputLabelProps={{ shrink: true }}
                                    inputProps={{ min: getMinDate() }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12 }} >
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={showEndDate}
                                            onChange={handleEndDateCheckboxChange}
                                            color="primary"
                                        />
                                    }
                                    label="Set Membership End Date"
                                />
                            </Grid>
                            {showEndDate && (
                                <Grid size={{ xs: 12, md: 6 }} >
                                    <TextField
                                        fullWidth
                                        label="Membership End Date"
                                        name="membershipEndDate"
                                        type="date"
                                        value={membershipEndDate}
                                        onChange={handleEndDateChange}
                                        InputLabelProps={{ shrink: true }}
                                        inputProps={{ min: membershipStartDate || getMinDate() }}
                                    />
                                </Grid>
                            )}
                        </Grid>
                    </Paper>

                    <Paper sx={{ p: 3, mb: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h5" gutterBottom sx={{ mb: 0 }}>
                                One-Time Fees
                            </Typography>
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<AddIcon />}
                                onClick={handleFeeDialogToggle}
                            >
                                Add One-Time Fee
                            </Button>
                        </Box>
                        <Divider sx={{ mb: 2 }} />

                        {selectedFees.length > 0 ? (
                            <List>
                                {selectedFees.map((fee) => (
                                    <ListItem
                                        key={fee.id}
                                        secondaryAction={
                                            <IconButton edge="end" aria-label="delete" onClick={() => handleRemoveFee(fee.id)}>
                                                <DeleteIcon />
                                            </IconButton>
                                        }
                                    >
                                        <ListItemText
                                            primary={fee.feeName}
                                            secondary={formatCurrency(fee.amount)}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        ) : (
                            <Typography variant="body1" color="textSecondary" sx={{ mb: 2 }}>
                                No one-time fees added
                            </Typography>
                        )}
                    </Paper>

                    <Paper sx={{ p: 3, mb: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h5" gutterBottom sx={{ mb: 0 }}>
                                Discounts
                            </Typography>
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<AddIcon />}
                                onClick={handleDiscountDialogToggle}
                            >
                                Add Discount
                            </Button>
                        </Box>
                        <Divider sx={{ mb: 2 }} />

                        {selectedDiscounts.length > 0 ? (
                            <List>
                                {selectedDiscounts.map((discount) => (
                                    <ListItem
                                        key={discount.id}
                                        secondaryAction={
                                            <IconButton edge="end" aria-label="delete" onClick={() => handleRemoveDiscount(discount.id)}>
                                                <DeleteIcon />
                                            </IconButton>
                                        }
                                    >
                                        <ListItemText
                                            primary={discount.name}
                                            secondary={discount.category === 'percentage' ?
                                                `${discount.value}%` :
                                                formatCurrency(discount.value)}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        ) : (
                            <Typography variant="body1" color="textSecondary" sx={{ mb: 2 }}>
                                No discounts added
                            </Typography>
                        )}
                    </Paper>

                    <Paper sx={{ p: 3, mb: 3 }}>
                        <Typography variant="h5" gutterBottom>
                            Summary
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12 }} >
                                <Typography variant="subtitle1">
                                    Base Monthly Price: {formatCurrency(formData.monthlyPrice)}
                                </Typography>
                            </Grid>

                            {selectedFees.length > 0 && (
                                <Grid size={{ xs: 12 }} >
                                    <Typography variant="subtitle1">
                                        One-Time Fees: {formatCurrency(selectedFees.reduce((sum, fee) => sum + parseFloat(fee.amount.toString()), 0))}
                                    </Typography>
                                </Grid>
                            )}

                            {selectedDiscounts.length > 0 && (
                                <Grid size={{ xs: 12 }} >
                                    <Typography variant="subtitle1">
                                        Discounts: -{formatCurrency(selectedDiscounts.reduce((sum, discount) => {
                                            if (discount.category === 'percentage') {
                                                return sum + (parseFloat(formData.monthlyPrice || '0') * parseFloat(discount.value.toString()) / 100);
                                            } else {
                                                return sum + parseFloat(discount.value.toString());
                                            }
                                        }, 0))}
                                    </Typography>
                                </Grid>
                            )}

                            <Grid size={{ xs: 12 }} >
                                <Typography variant="h6" fontWeight="bold">
                                    Total: {formatCurrency(calculateTotal())}
                                </Typography>
                            </Grid>
                        </Grid>
                    </Paper>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4, mb: 4 }}>
                        <Button
                            variant="outlined"
                            onClick={() => navigate(`/membership-applications/${id}`)}
                        >
                            Back to Application
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            type="submit"
                        >
                            Create Account
                        </Button>
                    </Box>
                </form>
            ) : (
                <Paper sx={{ p: 3 }}>
                    <Typography>Application not found</Typography>
                </Paper>
            )}

            {/* Fee Dialog */}
            <Dialog open={feeDialogOpen} disableEscapeKeyDown={true} maxWidth="sm" fullWidth>
                <DialogTitle>Add One-Time Fee</DialogTitle>
                <DialogContent>
                    <List>
                        {oneTimeFees.filter(fee => !selectedFees.some(selected => selected.id === fee.id)).map((fee) => (
                            <ListItem
                                key={fee.id}
                                onClick={() => handleAddFee(fee)}
                                sx={{ cursor: 'pointer' }}
                            >
                                <ListItemText
                                    primary={fee.feeName}
                                    secondary={`${fee.feeType} - ${formatCurrency(fee.amount)}`}
                                />
                            </ListItem>
                        ))}
                    </List>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleFeeDialogToggle} color="primary">
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Discount Dialog */}
            <Dialog open={discountDialogOpen} disableEscapeKeyDown={true} maxWidth="sm" fullWidth>
                <DialogTitle>Add Discount</DialogTitle>
                <DialogContent>
                    <List>                        {discounts.filter(discount => !selectedDiscounts.some(selected => selected.id === discount.id)).map((discount) => (<ListItem
                        key={discount.id}
                        onClick={() => handleAddDiscount(discount)}
                        sx={{ cursor: 'pointer' }}
                    >
                        <ListItemText
                            primary={discount.name}
                            secondary={discount.category === 'percentage' ?
                                `${discount.value}% (${discount.type})` :
                                `${formatCurrency(discount.value)} (${discount.type})`}
                        />
                    </ListItem>
                    ))}
                    </List>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDiscountDialogToggle} color="primary">
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default CreateMemberAccountPage;
